<template>
  <Transition name="fade">
    <div
      v-if="isReady && (store.apiAvailability || (!store.firstVisit && store.apiError))"
      ref="windowRef"
      v-ga-section-view="{
        interaction: 'chatbot_view',
      }"
      class="chatbot-area"
      :data-section-content="`v.${store.patchNotesVersion} Chat Bot`"
      data-section-name="Chat Bot"
      data-section-number="3"
    >
      <first-visit-overlay @click="inputBoxFocus = true" />
      <Transition mode="out-in" name="fade">
        <SurveyDialog v-if="surveyDialog" @close="surveyDialog = false" />
        <ChatbotStickyButton v-else-if="store.isWindowMinimized" @click="clickRestore" />
        <div
          v-else
          class="window-wrapper"
          :class="{
            maximized: store.isWindowMaximized,
            mobile,
            error: store.apiError,
            dialog: endDialog,
          }"
        >
          <div class="chatbot-window" @click="inputBoxFocus = true">
            <img class="beta-icon" src="~@/assets/imgs/chatbot/beta-icon.webp" />
            <img v-if="store.firstVisit" class="new-arrow" src="~@/assets/imgs/chatbot/new-arrow.webp" />
            <div class="header">
              <div class="window-buttons">
                <i-local-minimize
                  class="minimize-btn"
                  @click.stop="
                    () => {
                      gaShortcut('chatbot_minimize')
                      store.isWindowMinimized = true
                    }
                  "
                />
                <template v-if="!store.apiError && !mobile">
                  <i-local-maximize
                    v-if="!store.isWindowMaximized"
                    v-ga-event="{ interaction: 'chatbot_expand' }"
                    class="maximize-btn"
                    @click="store.isWindowMaximized = true"
                  />
                  <i-local-restore-down v-else class="restore-btn" @click="store.isWindowMaximized = false" />
                </template>
                <i-local-close class="close-btn" @click.stop="closeWindow" />
              </div>
              <div class="titles">
                <h1>PATCH NOTES CHAT</h1>
                <h2>GET ANSWERS FROM PAST YEAR (V.{{ store.oldestVersion }} TO V.{{ store.latestVersion }})</h2>
              </div>
            </div>
            <div class="notice roboto-medium">
              *Pink Bean can only answer questions about the v.{{ store.oldestVersion }} to v.{{ store.latestVersion }}
              Patch Notes.
            </div>
            <div class="chat-body roboto-regular" @click.stop>
              <div ref="chatScrollRef" class="scroll styled-scrollbar">
                <div class="disclaimer roboto-bold">
                  <img src="~@/assets/imgs/chatbot/disclaimer-icon.webp" />
                  <p>
                    Disclaimer: this is a Beta version! By using the feature, you acknowledge that the information
                    received may not be 100% accurate. Your chat history will be erased after you select "End Chat" and
                    you can start a new chat session.<br />
                    *Please do not provide any personal information when using this tool.
                  </p>
                </div>
                <template v-for="chat in store.chats" :key="chat.time">
                  <div v-if="chat.type === ChatType.Answer" class="answer">
                    <AsistantMessage :source="chat.message" />
                  </div>
                  <Transition name="fade">
                    <div v-if="chat.type === ChatType.Error" class="answer error">
                      Please refrain from using offensive language. We can answer your questions about Patch Notes from
                      the past year.
                    </div>
                  </Transition>
                  <Transition name="fade">
                    <div v-if="chat.type === ChatType.Question" class="question">
                      <pre>{{ chat.message }}</pre>
                    </div>
                  </Transition>
                </template>
                <div v-if="store.chats.length % 2 === 1 && store.streamMessage.length > 0" class="answer">
                  <AsistantMessage :source="store.streamMessage" />
                </div>
                <Transition name="fade">
                  <div v-if="waitingAnswer" class="answer waiting">
                    <img src="~@/assets/imgs/chatbot/generating-icon.webp" />
                    <span>Pink Bean is thinking...</span>
                  </div>
                </Transition>
                <!-- This needs to be placed after .question and .answer  -->
                <div class="clear-both" />
                <Transition name="fade">
                  <div v-if="store.systemError" key="error" class="system-error">
                    We are sorry, but the service is temporarily unavailable due to a system error. Please try again
                    later.
                  </div>
                </Transition>
              </div>
            </div>
            <div v-if="!store.apiError" class="chat-input roboto-medium" :class="{ disabled: !store.isReadyForAsking }">
              <AutoResizedTextarea
                ref="inputBox"
                v-model="store.questionInputbox"
                class="input-box"
                maxlength="300"
                placeholder="Ask a question! Ex: What’s new in the latest patch? (0/300)"
                @keydown.enter="keydown($event)"
              />
              <button @click="send"><i-ic-baseline-send /></button>
            </div>
            <div v-else class="init-error roboto-regular">
              We are sorry, but the service is temporarily unavailable due to a system error. Please try again later.
            </div>
          </div>
          <Transition name="fade">
            <EndDialog v-if="endDialog" @confirm="confirmEndDialog($event)" />
          </Transition>
        </div>
      </Transition>
    </div>
  </Transition>
</template>
<script setup lang="ts">
  import { ChatType, useChatbotStore } from '@/store/Chatbot'
  import { useGAEventStore } from '@/store/GAEventTracking'
  import { useFocus } from '@vueuse/core'
  const { mobile } = useDisplay()
  const route = useRoute()
  const inputBox = ref()
  const disableInputBox = ref(false)
  const windowRef = ref<HTMLElement>()
  const chatScrollRef = ref<HTMLBaseElement>()
  const isReady = ref(false)
  const endDialog = ref(false)
  const surveyDialog = ref(false)
  const store = useChatbotStore()
  const { focused: inputBoxFocus } = useFocus(inputBox)

  const waitingAnswer = computed(() => store.chats.length % 2 === 1 && store.streamMessage.length === 0)

  const props = defineProps({
    newsTitle: {
      type: String,
      required: true,
    },
  })

  onMounted(async () => {
    // const matcher = props.newsTitle.match(/v\.(\d+)/i)
    // if (!matcher) {
    //   console.error('Patch Note version not found in the news title')
    //   return
    // }
    // const patchNotesVersion = parseInt(matcher[1], 10)
    // console.log('patch notes version ====>', patchNotesVersion)
    await store.init()
    setTimeout(() => (isReady.value = true), 500) // delay for UI appearance
    document.body.classList.add('patch-notes')
    // }
  })

  onUnmounted(() => {
    document.body.classList.remove('patch-notes')
  })

  function keydown(event: KeyboardEvent) {
    if (event.code === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      send()
    }
  }

  const gaEvent = useGAEventStore()
  function gaShortcut(interaction: string) {
    gaEvent.pushActionEvent({ interaction }, windowRef.value)
  }

  function send() {
    if (!store.isReadyForAsking) return
    store.addQuestion()
    gaShortcut('chatbot_submit')
  }

  function closeWindow() {
    gaShortcut('chatbot_close')
    if (store.chats.length > 0) endDialog.value = true
    else store.isWindowMinimized = true // if there is no chat history, treats as minimize
  }

  function confirmEndDialog(result: boolean) {
    endDialog.value = false
    if (result) {
      gaShortcut('end_chat')
      surveyDialog.value = true
      store.isWindowMinimized = true
    }
  }

  function clickRestore() {
    gaShortcut('chatbot_reopen')
    store.firstVisit = false
  }

  watch([chatScrollRef], () => {
    if (!chatScrollRef.value) return
    inputBoxFocus.value = true
    // chatScrollRef.value.scrollTo({ top: chatScrollRef.value.scrollHeight, behavior: 'smooth' })
  })

  watch(
    () => [store.streamMessage.length],
    () => {
      if (chatScrollRef.value) {
        chatScrollRef.value.scrollTo({ top: chatScrollRef.value.scrollHeight, behavior: 'smooth' })
      }
    }
  )

  // 10 seconds delay to enable the input box (requested by Jisung)
  watch(
    () => store.systemError,
    (val) => {
      if (val) {
        disableInputBox.value = true
        setTimeout(() => {
          disableInputBox.value = false
        }, 10 * 1000)
      }
    }
  )

  watch(
    () => [chatScrollRef.value, store.chats.length, store.systemError, waitingAnswer, store.isWindowMaximized],
    () => {
      setTimeout(() => {
        if (chatScrollRef.value) {
          chatScrollRef.value.scrollTo({ top: chatScrollRef.value.scrollHeight, behavior: 'smooth' })
        }
      }, 500)
    }
  )

  // minimize the mobile UI, when the hashtag change detected (user clicks the anchor link or history back/forward)
  // for user to be able to see the actual navigated hashed section
  watch(
    () => route.hash,
    () => {
      if (mobile.value) store.isWindowMinimized = true
    }
  )
</script>

<style lang="scss">
  body.patch-notes {
    .scroll-to-top-btn {
      bottom: 80px !important;
    }
  }
</style>

<style lang="scss" scoped>
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
  .fade-enter-active {
    transition: opacity 0.3s;
  }
  .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .fade-in-enter-active {
    transition: opacity 0.3s;
  }
  .fade-in-enter-from {
    opacity: 0;
  }

  .roboto-regular {
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    font-style: normal;
  }
  .roboto-medium {
    font-family: 'Roboto', sans-serif;
    font-weight: 500;
    font-style: normal;
  }
  .roboto-bold {
    font-family: 'Roboto', sans-serif;
    font-weight: 700;
    font-style: normal;
  }
  .chatbot-area {
    position: fixed;
    z-index: 1000;
    right: 10px;
    bottom: 10px;
  }

  .window-wrapper {
    position: relative;
    width: 410px;
    height: 435px;
    min-height: 400px;
    transition: 0.3s;

    .chatbot-window {
      width: 100%;
      height: 100%;
      background-color: white;

      display: flex; // in order to use flex-grow on .chat-body
      flex-direction: column;

      transition: filter 0.3s;

      border-radius: 10px;
      box-shadow: 0px 4px 5px -2px rgba(0, 0, 0, 0.3), 0px 7px 10px 1px rgba(0, 0, 0, 0.2),
        0px -7px 10px 1px rgba(0, 0, 0, 0.2), 0px 2px 16px 1px rgba(0, 0, 0, 0.16) !important;
    }

    .beta-icon {
      position: absolute;
      top: -15px;
      left: -12px;
      z-index: 2;
      filter: drop-shadow(-3px -3px 5px rgba(0, 0, 0, 0.4));
    }
    .new-arrow {
      position: absolute;
      top: -70px;
      left: 80px;
      filter: drop-shadow(0px 0px 7px rgba(0, 0, 0, 0.8));
    }

    .window-buttons {
      position: absolute;
      right: 0;
      top: 0;
      display: flex;
      column-gap: 2px;
      padding: 4px 6px;
      svg {
        width: 20px;
        height: 20px;
        padding: 4px;
        color: white;
        opacity: 0.6;
        cursor: pointer;
        transition: opacity 0.2s;
        &:hover {
          opacity: 1;
        }
      }
      .minimize-btn {
        padding: 0;
      }
    }

    .header {
      position: relative;
      border-radius: 10px 10px 0 0;
      overflow: hidden;
      display: inline-table;
      flex-shrink: 0; // fix - chat input box shrink issue in safari browser

      .titles {
        padding: 8px 0 8px 20px;
        text-align: center;
        background: linear-gradient(to right, #ff6d00, #bf360c);
        color: white;
        h1 {
          font-size: 20px;
          margin: 0;
        }
        h2 {
          font-size: 15px;
          margin: 0;
          white-space: nowrap;
        }
      }
    }
    .notice {
      padding: 6px 0px;
      text-align: center;
      font-size: 12px;
      color: #bf360c;
      background-color: #ffc107;
    }
    .chat-body {
      transition: 0.3s;
      background-color: #eceff1;
      font-size: 14px;
      line-height: 16px;
      overflow: hidden;

      display: flex;
      flex-direction: column;
      justify-content: space-between;

      flex-grow: 1;

      .scroll {
        mask: linear-gradient(red 0 0) subtract,
          linear-gradient(0deg, red, transparent 10px calc(100% - 10px), red) content-box;
        padding: 0 10px;
        overflow-x: hidden;
        overflow-y: auto;
        &::after {
          content: '';
          display: block;
          height: 10px; // bottom padding
        }
      }
    }
    .chat-input {
      border-top: 2px solid #90a4ae;
      display: flex;
      flex-shrink: 0;
      align-items: center;
      padding: 0 8px;
      .input-box {
        font-size: 14px;
        padding: 0;
        margin: 14px 0 12px;
      }
      button {
        border-radius: 40px;
        width: 26px;
        aspect-ratio: 1/1;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        background-color: #ff6d00;
        transition: background-color 0.3s;
        svg {
          color: white;
          width: 14px;
          height: 14px;
          margin-left: 2px;
        }
        &:hover {
          background-color: #bf360c;
        }
      }
      &.disabled {
        button {
          background-color: #b0bec5;
        }
      }
    }

    &.maximized {
      height: calc(100vh - 70px);
      min-height: calc(100vh - 70px);
    }

    // transition: min-height 0.3s, min-width 0.3s, left 0.3s;
    /// mobile
    &.mobile {
      position: fixed;
      width: 100vw;
      min-height: initial;
      height: auto; //calc(100% - env(keyboard-inset-height));
      top: 0;
      left: 0;
      bottom: 0;

      .beta-icon {
        display: none;
      }
      .notice {
        display: block;
      }
      .chat-input {
        display: flex;
      }
      .header {
        // height: 50px;
        .titles {
          h1 {
            font-size: calc(15px + 0.7em);
          }
          h2 {
            font-size: calc(6px + 0.7em);
          }
        }
        .window-buttons {
          .minimize-btn {
            width: 22px;
            height: 22px;
          }
        }
      }
      // }
    }

    &.error {
      max-height: 150px;
      min-height: 0;
      top: auto;
      bottom: 0;
      .chat-body {
        display: none;
      }
    }
    &.dialog {
      .chatbot-window {
        filter: brightness(0.5);
      }
    }
  }

  .init-error {
    font-size: 14px;
    padding: 10px 30px;
    text-align: center;
  }

  .system-error {
    font-size: 14px;
    padding: 40px 10px;
    text-align: center;
  }

  .question,
  .answer {
    color: white;
    padding: 10px;
    border-radius: 5px;
    max-width: 320px;
  }
  .question + .answer {
    margin-top: 10px;
  }

  .answer + .question {
    margin-top: 10px;
  }

  .question {
    background-color: #bf360c;
    float: right;
    clear: both;
    pre {
      white-space: pre-line;
      overflow-wrap: break-word;
      font-family: 'Roboto', sans-serif;
    }
  }

  .clear-both {
    clear: both;
  }

  .answer {
    float: left;
    clear: both;
    background-color: #ff6d00;
    position: relative;

    // .thumbs {
    //   position: absolute;
    //   top: 100%;
    //   right: 0;
    //   margin-top: 10px;
    //   display: flex;
    //   column-gap: 10px;
    //   color: #90a4ae;
    //   svg {
    //     width: 30px;
    //     height: 30px;
    //     cursor: pointer;
    //     &:hover,
    //     &.selected {
    //       color: #bf360c;
    //     }
    //   }
    //   &.rated {
    //     pointer-events: none;
    //   }
    // }

    &.error {
      display: flex;
      column-gap: 10px;
      align-items: center;
      &::before {
        content: url('@/assets/imgs/chatbot/wrong-question-icon.webp');
      }
    }
  }

  .disclaimer {
    text-align: center;
    padding: 20px;
    img {
      margin-bottom: 20px;
    }
  }
  .mobile {
    .disclaimer {
      padding: 30px 10px;
    }
  }

  .waiting {
    display: flex;
    align-items: center;
    span {
      padding: 0 20px;
    }
  }
</style>
